import React, { Component } from 'react';
import { ArrowRight, Shield, TrendingUp, CheckCircle, ChevronDown, Languages } from 'lucide-react';
interface HeroProps {
  language: 'en' | 'es';
}
export function Hero({
  language
}: HeroProps) {
  const content = {
    en: {
      headlineLine1: 'Fuel Your Business Growth',
      headlineLine2: 'Fast, Flexible, and Backed by Real Capital.',
      subheadline: 'Fast, flexible business funding for real companies — not personal loans. Minimum $10,000/month in business revenue. No credit pull. 24-hour decisions.',
      description: 'We help real business owners across the U.S. access working capital fast — for payroll, inventory, and growth. Simple. Transparent. Human.',
      trustBadge: 'No Credit Pull – This Will Not Affect Your Score',
      cta1: 'Apply Now',
      cta1Full: 'Apply Now — No Credit Pull',
      badge1: 'Trusted by 1,000+ Businesses',
      badge2: 'Funding in 24 Hours',
      badge3: 'English + Español',
      security: 'Backed by Amerify — Providing Millions in Business Funding Daily Across the U.S.',
      scrollText: 'See how it works',
      seHabla: 'Se habla español'
    },
    es: {
      headlineLine1: 'Impulse el Crecimiento de Su Negocio',
      headlineLine2: 'Rápido, Flexible y Respaldado por Capital Real.',
      subheadline: 'Financiamiento empresarial rápido y flexible para empresas reales — no préstamos personales. Mínimo $10,000/mes en ingresos comerciales. Sin revisión de crédito. Decisiones en 24 horas.',
      description: 'Ayudamos a verdaderos dueños de negocios en todo EE.UU. a acceder capital de trabajo rápido — para nómina, inventario y crecimiento. Simple. Transparente. Humano.',
      trustBadge: 'Sin Revisión de Crédito – No Afectará Su Puntaje',
      cta1: 'Solicitar Ahora',
      cta1Full: 'Solicitar Ahora — Sin Revisión',
      badge1: 'Confiado por 1,000+ Negocios',
      badge2: 'Fondos en 24 Horas',
      badge3: 'English + Español',
      security: 'Respaldado por Amerify — Proporcionando Millones en Financiamiento Comercial Diariamente en EE.UU.',
      scrollText: 'Ver cómo funciona',
      seHabla: 'We speak English'
    }
  };
  const t = content[language];
  const scrollToForm = () => {
    const element = document.getElementById('apply');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="home" className="relative w-full min-h-screen flex items-center bg-gradient-to-br from-[#0A1929] via-[#142B44] to-[#1A365D] text-white overflow-hidden" data-id="element-107">
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40" data-id="element-108"></div>
      <div className="absolute inset-0" data-id="element-109">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1600')] bg-cover bg-center opacity-10 blur-sm" data-id="element-110"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#2D5D9F] rounded-full filter blur-3xl opacity-10" data-id="element-111"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#4A90E2] rounded-full filter blur-3xl opacity-10" data-id="element-112"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-id="element-113">
        <div className="max-w-4xl" data-id="element-114">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 sm:mb-8 animate-fade-in" data-id="element-115">
            <div className="flex items-center space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20" data-id="element-116">
              <Shield className="text-white flex-shrink-0" size={16} data-id="element-117" />
              <span className="text-xs sm:text-sm font-medium text-white whitespace-nowrap" data-id="element-118">
                {t.badge1}
              </span>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20" data-id="element-119">
              <TrendingUp className="text-white flex-shrink-0" size={16} data-id="element-120" />
              <span className="text-xs sm:text-sm font-medium text-white whitespace-nowrap" data-id="element-121">
                {t.badge2}
              </span>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-[#25D366] to-[#20BA5A] backdrop-blur-sm rounded-full border border-white/30 shadow-lg" data-id="element-122">
              <Languages className="text-white flex-shrink-0" size={16} data-id="element-123" />
              <span className="text-xs sm:text-sm font-bold text-white whitespace-nowrap" data-id="element-124">
                {t.badge3}
              </span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-2 sm:mb-3 leading-tight tracking-tight animate-fade-in-up" data-id="element-125">
            {t.headlineLine1}
          </h1>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-6 sm:mb-10 leading-tight tracking-tight animate-fade-in-up animation-delay-200" data-id="element-126">
            {t.headlineLine2}
          </h1>
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-semibold mb-6 sm:mb-8 text-white/95 leading-relaxed animate-fade-in-up animation-delay-400" data-id="element-127">
            {t.subheadline}
          </h2>
          <div className="inline-flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8 animate-fade-in-up animation-delay-600" data-id="element-128">
            <div className="inline-flex items-start sm:items-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-green-500/20 backdrop-blur-sm rounded-xl border-2 border-green-400" data-id="element-129">
              <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5 sm:mt-0" size={20} data-id="element-130" />
              <span className="text-sm sm:text-base lg:text-lg font-bold text-white leading-snug" data-id="element-131">
                {t.trustBadge}
              </span>
            </div>
          </div>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 text-gray-200 leading-relaxed max-w-3xl animate-fade-in-up animation-delay-600" data-id="element-132">
            {t.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6 animate-fade-in-up animation-delay-800" data-id="element-133">
            <button onClick={scrollToForm} className="group inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-white text-[#142B44] font-bold text-base sm:text-lg rounded-xl hover:bg-gray-100 transition-all shadow-2xl hover:shadow-3xl hover:scale-105 duration-300" data-id="element-134">
              <span className="sm:hidden" data-id="element-135">{t.cta1}</span>
              <span className="hidden sm:inline" data-id="element-136">{t.cta1Full}</span>
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} data-id="element-137" />
            </button>
          </div>
          <p className="text-sm text-gray-300 mb-6 sm:mb-8 italic animate-fade-in-up animation-delay-800" data-id="element-138">
            {t.seHabla}
          </p>
          <p className="text-xs sm:text-sm text-gray-300 mb-6 sm:mb-8 animate-fade-in-up animation-delay-800 leading-relaxed" data-id="element-139">
            {t.security}
          </p>
          <button onClick={scrollToHowItWorks} className="flex items-center space-x-2 text-white hover:text-gray-200 transition-colors animate-bounce mx-auto sm:mx-0" data-id="element-140">
            <span className="text-sm font-medium" data-id="element-141">{t.scrollText}</span>
            <ChevronDown size={20} data-id="element-142" />
          </button>
        </div>
      </div>
    </section>;
}